import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from "recharts";

// ─── Maghil Design Tokens ────────────────────────────────────────────────────
const T = {
  bg: "#F5F6FA",
  surface: "#FFFFFF",
  card: "#FFFFFF",
  cardBorder: "#E2E5EC",
  hover: "#F0F1F5",
  accent: "#D23A3B",
  accentSoft: "rgba(210,58,59,0.08)",
  accentGlow: "rgba(210,58,59,0.15)",
  green: "#16A34A",
  greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626",
  redSoft: "rgba(220,38,38,0.08)",
  orange: "#D97706",
  orangeSoft: "rgba(217,119,6,0.08)",
  t1: "#1A1D2E",
  t2: "#5A6178",
  t3: "#9CA3B8",
  divider: "#E8EAF0",
  shadow: "0 1px 3px rgba(0,0,0,0.06)",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

// ─── Material Symbols Rounded Icon ───────────────────────────────────────────
function MIcon({ name, size = 20, fill = 0, weight = 400, color = T.t3, style = {} }: any) {
  return (
    <span
      className="material-symbols-rounded"
      style={{
        fontSize: size,
        color,
        fontVariationSettings: `'FILL' ${fill}, 'wght' ${weight}, 'opsz' ${size}`,
        lineHeight: 1,
        userSelect: "none",
        ...style,
      }}
    >
      {name}
    </span>
  );
}

// ─── Mock Data ───────────────────────────────────────────────────────────────
const MOCK = {
  eventName: "Thanksgiving 2025",
  eventDates: "Nov 22 – Nov 28",
  selectedLocation: "Downtown Main St",
  kpis: {
    totalRevenue: { value: "$48,320", delta: "+18.4%", up: true },
    totalOrders: { value: "612", delta: "+22.1%", up: true },
    avgOrderValue: { value: "$78.95", delta: "+6.3%", up: true },
    cancellations: { value: "18", delta: "-4.2%", up: true },
    fulfillmentRate: { value: "96.8%", delta: "+1.2%", up: true },
    noShows: { value: "7", delta: "-3", up: true },
  },
  itemQuantities: [
    { name: "Mashed Potatoes (Fam)", qty: 201 },
    { name: "Pumpkin Pie (Whole)", qty: 187 },
    { name: "Gravy (Quart)", qty: 178 },
    { name: "Dinner Rolls (Doz)", qty: 165 },
    { name: "Cranberry Stuffing", qty: 156 },
    { name: "Whole Turkey (12lb)", qty: 142 },
    { name: "Green Bean Casserole", qty: 134 },
    { name: "Sweet Potato Cass.", qty: 112 },
    { name: "Honey Glazed Ham", qty: 98 },
    { name: "Mac & Cheese (Fam)", qty: 94 },
  ],
  pickupSlots: [
    { slot: "10 AM", orders: 12, capacity: 25 },
    { slot: "11 AM", orders: 22, capacity: 25 },
    { slot: "12 PM", orders: 24, capacity: 25 },
    { slot: "1 PM", orders: 31, capacity: 30 },
    { slot: "2 PM", orders: 35, capacity: 30 },
    { slot: "3 PM", orders: 42, capacity: 35 },
    { slot: "4 PM", orders: 52, capacity: 40 },
    { slot: "5 PM", orders: 55, capacity: 40 },
    { slot: "6 PM", orders: 22, capacity: 30 },
  ],
  bookingCurve: [
    { day: "D-14", current: 12, prior: 8 },
    { day: "D-12", current: 31, prior: 22 },
    { day: "D-10", current: 72, prior: 52 },
    { day: "D-8", current: 135, prior: 96 },
    { day: "D-6", current: 248, prior: 178 },
    { day: "D-4", current: 385, prior: 296 },
    { day: "D-2", current: 532, prior: 421 },
    { day: "D-0", current: 612, prior: 502 },
  ],
  channelMix: [
    { name: "Maghil Online", value: 62, color: T.accent },
    { name: "Phone Orders", value: 18, color: T.orange },
    { name: "Walk-In", value: 12, color: T.green },
    { name: "Third-Party", value: 8, color: T.t3 },
  ],

  fulfillmentStatus: [
    { status: "Picked Up", count: 542, pct: 88.6, color: T.green },
    { status: "Ready", count: 28, pct: 4.6, color: T.accent },
    { status: "Preparing", count: 17, pct: 2.8, color: T.orange },
    { status: "No-Show", count: 7, pct: 1.1, color: T.red },
    { status: "Cancelled", count: 18, pct: 2.9, color: T.t3 },
  ],
  customerRunSheet: [
    { id: "ORD-2847", name: "Sarah Mitchell", phone: "(201) 555-0142", slot: "4:00 PM", items: "Turkey, Pie, Gravy x2", total: "$142.50", payment: "Fully Paid", fulfillment: "Ready" },
    { id: "ORD-2848", name: "James Rodriguez", phone: "(201) 555-0198", slot: "4:00 PM", items: "Ham, Mac & Cheese, Rolls", total: "$98.75", payment: "Fully Paid", fulfillment: "Picked Up" },
    { id: "ORD-2849", name: "Linda Chen", phone: "(201) 555-0267", slot: "4:30 PM", items: "Turkey, Stuffing, Pecan Pie", total: "$168.00", payment: "Deposit Paid", fulfillment: "Preparing" },
    { id: "ORD-2850", name: "Michael Thompson", phone: "(201) 555-0334", slot: "5:00 PM", items: "Turkey, Potatoes, Gravy", total: "$124.25", payment: "Fully Paid", fulfillment: "Ordered" },
    { id: "ORD-2851", name: "Emily Davis", phone: "(201) 555-0089", slot: "5:00 PM", items: "Ham, Sweet Potato, Apple Pie", total: "$112.50", payment: "Fully Paid", fulfillment: "No-Show" },
  ],
};

// ─── Navigation Config (Bottom Nav) ──────────────────────────────────────────
const BOTTOM_TABS = [
  { icon: "electric_bolt", label: "Snapshot", active: false },
  { icon: "bar_chart", label: "Reports", active: true },
  { icon: "auto_awesome", label: "Brisque AI", active: false },
  { icon: "chat_bubble", label: "Support", active: false },
];

// ─── Drill-In Screens ────────────────────────────────────────────────────────
const DRILL_SCREENS = {
  MAIN: "main",
  PREP_LIST: "prep_list",
  PICKUP_SLOTS: "pickup_slots",
  BOOKING_CURVE: "booking_curve",
  CUSTOMER_SHEET: "customer_sheet",
};

// ─── Reusable Components ─────────────────────────────────────────────────────

function MobileCard({ children, style = {}, drillable = false, onClick }: any) {
  return (
    <div
      onClick={onClick}
      style={{
        background: T.card,
        border: drillable ? `1px solid rgba(210,58,59,0.20)` : `1px solid ${T.cardBorder}`,
        borderRadius: 14,
        padding: 14,
        boxShadow: T.shadow,
        position: "relative",
        ...style,
      }}
    >
      {drillable && (
        <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", top: 8, right: 8, opacity: 0.45 }}>
          <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {children}
    </div>
  );
}

function MobileKpiCard({ label, value, delta, up, drillable = false, onClick }: any) {
  const deltaColor = up ? T.green : T.red;
  const deltaArrow = up ? "↑" : "↓";
  return (
    <MobileCard drillable={drillable} onClick={onClick} style={{ textAlign: "center", padding: "12px 10px" }}>
      <div style={{ fontSize: 8, fontWeight: 400, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginTop: 4 }}>{value}</div>
      {delta && (
        <div style={{ fontSize: 9, fontWeight: 600, color: deltaColor, marginTop: 3 }}>
          {deltaArrow} {delta}
        </div>
      )}
    </MobileCard>
  );
}

function SectionTitle({ children, subtitle }: any) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{children}</div>
      {subtitle && <div style={{ fontSize: 10, color: T.t3, marginTop: 1 }}>{subtitle}</div>}
    </div>
  );
}

function StatusPill({ status }: any) {
  const map = {
    "Fully Paid": { bg: T.greenSoft, color: T.green },
    "Deposit Paid": { bg: T.orangeSoft, color: T.orange },
    "Unpaid": { bg: T.redSoft, color: T.red },
    "Refunded": { bg: T.hover, color: T.t3 },
    "Picked Up": { bg: T.greenSoft, color: T.green },
    "Ready": { bg: T.accentSoft, color: T.accent },
    "Preparing": { bg: T.orangeSoft, color: T.orange },
    "Ordered": { bg: T.hover, color: T.t2 },
    "No-Show": { bg: T.redSoft, color: T.red },
    "Cancelled": { bg: T.hover, color: T.t3 },
  };
  const s = map[status] || { bg: T.hover, color: T.t3 };
  return (
    <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>
      {status}
    </span>
  );
}

function DrillHeader({ title, subtitle, onBack }: any) {
  return (
    <div style={{ padding: "8px 16px 0", background: T.surface }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div
          onClick={onBack}
          style={{ width: 28, height: 28, borderRadius: 8, background: T.hover, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
        >
          <MIcon name="arrow_back" size={16} color={T.t2} weight={500} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 11, color: T.t3 }}>Events</span>
          <span style={{ fontSize: 11, color: T.t3 }}>›</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: T.t1 }}>{title}</span>
        </div>
      </div>
      {subtitle && <div style={{ fontSize: 10, color: T.t3, marginLeft: 36, marginBottom: 4 }}>{subtitle}</div>}
    </div>
  );
}

function MTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "6px 10px", boxShadow: T.shadow, fontFamily: T.font }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: T.t1, marginBottom: 3 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 9, color: T.t2, display: "flex", alignItems: "center", gap: 4, marginTop: 1 }}>
          <span style={{ width: 5, height: 5, borderRadius: 1, background: p.color, flexShrink: 0 }} />
          {p.name}: <span style={{ fontWeight: 600, color: T.t1 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Bottom Navigation ───────────────────────────────────────────────────────

function BottomNav() {
  return (
    <div style={{ height: 72, background: T.surface, borderTop: `1px solid ${T.divider}`, display: "flex", justifyContent: "space-around", alignItems: "center", paddingBottom: 8, position: "sticky", bottom: 0, zIndex: 20 }}>
      {BOTTOM_TABS.map((tab, i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "4px 14px", cursor: "pointer" }}>
          <MIcon name={tab.icon} size={22} fill={tab.active ? 1 : 0} weight={tab.active ? 600 : 400} color={tab.active ? T.accent : T.t3} style={{ opacity: tab.active ? 1 : 0.4 }} />
          <span style={{ fontSize: 9.5, fontWeight: tab.active ? 700 : 400, color: tab.active ? T.accent : T.t3 }}>{tab.label}</span>
          {tab.active && <div style={{ width: 4, height: 4, borderRadius: "50%", background: T.accent, boxShadow: `0 0 6px ${T.accentGlow}` }} />}
        </div>
      ))}
    </div>
  );
}

// ─── Main Screen: Event Overview ─────────────────────────────────────────────

function MainScreen({ onNavigate }: any) {
  return (
    <>
      {/* Header */}
      <div style={{ padding: "8px 20px", background: T.surface }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.t1 }}>Events</div>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: T.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff" }}>JM</div>
        </div>

        {/* Event Selector */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.accentSoft, borderRadius: 8, padding: "6px 10px", marginTop: 8, border: `1px solid rgba(210,58,59,0.12)` }}>
          <MIcon name="celebration" size={14} color={T.accent} fill={1} />
          <span style={{ fontSize: 11, fontWeight: 600, color: T.accent, flex: 1 }}>{MOCK.eventName}</span>
          <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={T.accent} strokeWidth="1.3" strokeLinecap="round" /></svg>
        </div>

        {/* Outlet + Date */}
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, background: T.surface, border: `1px solid ${T.divider}`, borderRadius: 8, padding: "5px 10px", flex: 1 }}>
            <span style={{ fontSize: 10 }}>📍</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.t1, flex: 1 }}>{MOCK.selectedLocation}</span>
            <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={T.t3} strokeWidth="1.3" strokeLinecap="round" /></svg>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, background: T.surface, border: `1px solid ${T.divider}`, borderRadius: 8, padding: "5px 10px" }}>
            <MIcon name="calendar_today" size={12} color={T.t3} />
            <span style={{ fontSize: 11, color: T.t3 }}>{MOCK.eventDates}</span>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>

        {/* KPI Row: Revenue, Orders, AOV */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <MobileKpiCard label="Event Revenue" value={MOCK.kpis.totalRevenue.value} delta={MOCK.kpis.totalRevenue.delta} up />
          <MobileKpiCard label="Total Orders" value={MOCK.kpis.totalOrders.value} delta={MOCK.kpis.totalOrders.delta} up />
          <MobileKpiCard label="Avg Order" value={MOCK.kpis.avgOrderValue.value} delta={MOCK.kpis.avgOrderValue.delta} up />
        </div>

        {/* KPI Grid Row 1 */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <MobileKpiCard label="Fulfillment" value={MOCK.kpis.fulfillmentRate.value} delta={MOCK.kpis.fulfillmentRate.delta} up />
          <MobileKpiCard label="No-Shows" value={MOCK.kpis.noShows.value} delta={MOCK.kpis.noShows.delta} up drillable />
          <MobileKpiCard label="Cancelled" value={MOCK.kpis.cancellations.value} delta={MOCK.kpis.cancellations.delta} up drillable />
        </div>

        {/* Prep Quantity List (Preview) */}
        <MobileCard drillable onClick={() => onNavigate(DRILL_SCREENS.PREP_LIST)}>
          <SectionTitle subtitle="Top items by quantity ordered">Prep Quantity List</SectionTitle>
          {(() => {
            const top6 = MOCK.itemQuantities.slice(0, 6);
            const maxQty = Math.max(...top6.map(d => d.qty));
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {top6.map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 10, color: T.t2, minWidth: 100, textAlign: "right", flexShrink: 0 }}>{item.name}</span>
                    <div style={{ flex: 1, height: 10, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(item.qty / maxQty) * 100}%`, background: i === 0 ? T.accent : "rgba(210,58,59,0.4)", borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: T.t1, minWidth: 28, textAlign: "right" }}>{item.qty}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </MobileCard>

        {/* Pickup Slots (Preview) */}
        <MobileCard drillable onClick={() => onNavigate(DRILL_SCREENS.PICKUP_SLOTS)}>
          <SectionTitle subtitle="Order volume by pickup time">Pickup Slot Volume</SectionTitle>
          <div style={{ height: 100 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={MOCK.pickupSlots} margin={{ left: -10, right: 5, top: 0, bottom: 0 }}>
                <XAxis dataKey="slot" tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Bar dataKey="orders" radius={[3, 3, 0, 0]} maxBarSize={18}>
                  {(() => {
                    const peak = Math.max(...MOCK.pickupSlots.map(s => s.orders));
                    return MOCK.pickupSlots.map((s, i) => (
                      <Cell key={i} fill={s.orders === peak ? T.accent : "rgba(210,58,59,0.4)"} />
                    ));
                  })()}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </MobileCard>

        {/* Booking Curve (Preview) */}
        <MobileCard drillable onClick={() => onNavigate(DRILL_SCREENS.BOOKING_CURVE)}>
          <SectionTitle subtitle="Cumulative orders vs. prior year">Booking Curve</SectionTitle>
          <div style={{ height: 100 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={MOCK.bookingCurve} margin={{ left: -10, right: 5, top: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="mCurveGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={T.accent} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={T.accent} stopOpacity={0.01} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={{ fontSize: 7, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Area type="monotone" dataKey="prior" stroke={T.t3} strokeWidth={1} strokeDasharray="4 2" fill="none" dot={false} />
                <Area type="monotone" dataKey="current" stroke={T.accent} strokeWidth={2} fill="url(#mCurveGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 6, justifyContent: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 12, height: 2, background: T.accent, borderRadius: 1 }} />
              <span style={{ fontSize: 8, color: T.t2 }}>2025</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 12, height: 2, background: T.t3, borderRadius: 1, opacity: 0.5 }} />
              <span style={{ fontSize: 8, color: T.t3 }}>2024</span>
            </div>
          </div>
        </MobileCard>

        {/* Channel Mix */}
        <MobileCard>
          <SectionTitle subtitle="Order source distribution">Channel Mix</SectionTitle>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 90, height: 90, flexShrink: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={MOCK.channelMix} cx="50%" cy="50%" innerRadius={28} outerRadius={40} dataKey="value" strokeWidth={0}>
                    {MOCK.channelMix.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
              {MOCK.channelMix.map((c, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 1, background: c.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 10, color: T.t2, flex: 1 }}>{c.name}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: T.t1 }}>{c.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </MobileCard>

        {/* Fulfillment Status */}
        <MobileCard>
          <SectionTitle subtitle="Order lifecycle tracking">Fulfillment</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {MOCK.fulfillmentStatus.map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <StatusPill status={f.status} />
                <div style={{ flex: 1, height: 5, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${f.pct}%`, background: f.color, borderRadius: 3, opacity: 0.7 }} />
                </div>
                <span style={{ fontSize: 9, fontWeight: 600, color: T.t1, minWidth: 20, textAlign: "right" }}>{f.count}</span>
              </div>
            ))}
          </div>
        </MobileCard>

        {/* Customer Run Sheet Shortcut */}
        <MobileCard drillable onClick={() => onNavigate(DRILL_SCREENS.CUSTOMER_SHEET)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px" }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: T.accentSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MIcon name="assignment" size={20} color={T.accent} fill={1} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.t1 }}>Customer Run Sheet</div>
            <div style={{ fontSize: 10, color: T.t3, marginTop: 1 }}>{MOCK.customerRunSheet.length} orders — tap to view, search, or export</div>
          </div>
        </MobileCard>

      </div>
    </>
  );
}

// ─── Drill-In: Prep Quantity List ────────────────────────────────────────────

function PrepListDrillIn({ onBack }: any) {
  return (
    <>
      <DrillHeader title="Prep List" subtitle={`${MOCK.eventName} — ${MOCK.eventDates}`} onBack={onBack} />
      <div style={{ padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        {/* Summary */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <MobileCard style={{ textAlign: "center", padding: "10px" }}>
            <div style={{ fontSize: 7, color: T.t3, textTransform: "uppercase" }}>Total Items</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.t1, marginTop: 3 }}>12</div>
          </MobileCard>
          <MobileCard style={{ textAlign: "center", padding: "10px" }}>
            <div style={{ fontSize: 7, color: T.t3, textTransform: "uppercase" }}>Total Units</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.t1, marginTop: 3 }}>1,632</div>
          </MobileCard>
          <MobileCard style={{ textAlign: "center", padding: "10px" }}>
            <div style={{ fontSize: 7, color: T.t3, textTransform: "uppercase" }}>Est. Revenue</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.accent, marginTop: 3 }}>$33.5K</div>
          </MobileCard>
        </div>

        {/* Full Chart */}
        <MobileCard>
          <SectionTitle subtitle="All items sorted by quantity">Item Quantities</SectionTitle>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={MOCK.itemQuantities} layout="vertical" margin={{ left: 120, right: 10, top: 0, bottom: 0 }}>
                <CartesianGrid horizontal={false} stroke={T.divider} strokeOpacity={0.5} />
                <XAxis type="number" tick={{ fontSize: 8, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: T.t1, fontFamily: T.font }} axisLine={false} tickLine={false} width={118} />
                <Tooltip content={<MTooltip />} />
                <Bar dataKey="qty" fill={T.accent} radius={[0, 3, 3, 0]} maxBarSize={16}>
                  {MOCK.itemQuantities.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? T.accent : `rgba(210,58,59,0.4)`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </MobileCard>

        {/* Item List (expandable-style) */}
        <MobileCard>
          <SectionTitle subtitle="Tap any item to see customers who ordered it">Order Detail</SectionTitle>
          {MOCK.itemQuantities.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: i < MOCK.itemQuantities.length - 1 ? `1px solid ${T.divider}` : "none" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: T.t1 }}>{item.name}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{item.qty}</div>
                <div style={{ fontSize: 8, color: T.t3 }}>units</div>
              </div>
              <svg width="8" height="8" viewBox="0 0 10 10" style={{ opacity: 0.3 }}>
                <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          ))}
        </MobileCard>
      </div>
    </>
  );
}

// ─── Drill-In: Pickup Slots ─────────────────────────────────────────────────

function PickupSlotsDrillIn({ onBack }: any) {
  const peak = Math.max(...MOCK.pickupSlots.map(s => s.orders));
  return (
    <>
      <DrillHeader title="Pickup Slots" subtitle="Order volume by pickup time" onBack={onBack} />
      <div style={{ padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        <MobileCard>
          <SectionTitle>Orders by Slot</SectionTitle>
          <div style={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={MOCK.pickupSlots} margin={{ left: -10, right: 5, top: 5, bottom: 5 }}>
                <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5} />
                <XAxis dataKey="slot" tick={{ fontSize: 8, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 8, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
                <Tooltip content={<MTooltip />} />
                <Bar dataKey="orders" name="Orders" radius={[3, 3, 0, 0]} maxBarSize={18}>
                  {MOCK.pickupSlots.map((s, i) => (
                    <Cell key={i} fill={s.orders === peak ? T.accent : "rgba(210,58,59,0.4)"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </MobileCard>

        {/* Slot Detail List */}
        <MobileCard>
          <SectionTitle subtitle="Tap a slot to see orders">Slot Breakdown</SectionTitle>
          {MOCK.pickupSlots.map((s, i) => {
            const pctOfPeak = Math.round((s.orders / peak) * 100);
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: i < MOCK.pickupSlots.length - 1 ? `1px solid ${T.divider}` : "none" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.t1, minWidth: 45 }}>{s.slot}</span>
                <div style={{ flex: 1, height: 5, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pctOfPeak}%`, background: s.orders === peak ? T.accent : "rgba(210,58,59,0.5)", borderRadius: 3 }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: s.orders === peak ? T.accent : T.t1 }}>{s.orders}</span>
                <span style={{ fontSize: 9, color: T.t3 }}>orders</span>
              </div>
            );
          })}
        </MobileCard>
      </div>
    </>
  );
}

// ─── Drill-In: Booking Curve ─────────────────────────────────────────────────

function BookingCurveDrillIn({ onBack }: any) {
  return (
    <>
      <DrillHeader title="Booking Curve" subtitle="Cumulative pre-order pacing" onBack={onBack} />
      <div style={{ padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <MobileCard style={{ textAlign: "center", padding: "10px" }}>
            <div style={{ fontSize: 7, color: T.t3, textTransform: "uppercase" }}>2025 Total</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.accent, marginTop: 3 }}>612</div>
          </MobileCard>
          <MobileCard style={{ textAlign: "center", padding: "10px" }}>
            <div style={{ fontSize: 7, color: T.t3, textTransform: "uppercase" }}>2024 Total</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.t3, marginTop: 3 }}>502</div>
          </MobileCard>
        </div>

        <MobileCard>
          <SectionTitle subtitle="Orders placed from pre-order open to event day">Pacing Comparison</SectionTitle>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={MOCK.bookingCurve} margin={{ left: 0, right: 5, top: 5, bottom: 5 }}>
                <defs>
                  <linearGradient id="mCurveGrad2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={T.accent} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={T.accent} stopOpacity={0.01} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5} />
                <XAxis dataKey="day" tick={{ fontSize: 8, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 8, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} width={30} />
                <Tooltip content={<MTooltip />} />
                <Area type="monotone" dataKey="prior" name="2024" stroke={T.t3} strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={false} />
                <Area type="monotone" dataKey="current" name="2025" stroke={T.accent} strokeWidth={2.5} fill="url(#mCurveGrad2)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </MobileCard>

        <MobileCard>
          <SectionTitle>Key Insight</SectionTitle>
          <div style={{ fontSize: 11, color: T.t2, lineHeight: 1.5 }}>
            Pre-order pacing is <span style={{ fontWeight: 600, color: T.accent }}>21.9% ahead</span> of last year at the same point. The steepest booking acceleration occurred between D-8 and D-5, suggesting marketing campaigns launched 7–8 days out are most effective.
          </div>
        </MobileCard>
      </div>
    </>
  );
}

// ─── Drill-In: Customer Run Sheet ────────────────────────────────────────────

function CustomerSheetDrillIn({ onBack }: any) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const statuses = ["All", "Ready", "Preparing", "Ordered", "Picked Up", "No-Show"];
  const filtered = MOCK.customerRunSheet.filter(c => {
    const ms = !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.id.toLowerCase().includes(search.toLowerCase());
    const mf = filter === "All" || c.fulfillment === filter;
    return ms && mf;
  });

  return (
    <>
      <DrillHeader title="Run Sheet" subtitle="Customer order manifest" onBack={onBack} />
      <div style={{ padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>

        {/* Search */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: "8px 12px", boxShadow: T.shadow }}>
          <MIcon name="search" size={16} color={T.t3} />
          <input
            type="text"
            placeholder="Search name or order ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ border: "none", outline: "none", background: "transparent", fontSize: 12, color: T.t1, fontFamily: T.font, width: "100%" }}
          />
        </div>

        {/* Status Filters */}
        <div style={{ display: "flex", gap: 5, overflowX: "auto", paddingBottom: 4 }}>
          {statuses.map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              style={{
                fontSize: 10, fontWeight: filter === s ? 600 : 400, fontFamily: T.font,
                color: filter === s ? T.accent : T.t3,
                background: filter === s ? T.accentSoft : "transparent",
                border: "none", borderRadius: 4, padding: "3px 8px", cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {s}
            </button>
          ))}
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 3, padding: "3px 8px", borderRadius: 4, border: `1px solid ${T.cardBorder}`, cursor: "pointer" }}>
              <MIcon name="print" size={12} color={T.t3} />
              <span style={{ fontSize: 9, color: T.t3 }}>Print</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 3, padding: "3px 8px", borderRadius: 4, border: `1px solid ${T.cardBorder}`, cursor: "pointer" }}>
              <MIcon name="download" size={12} color={T.t3} />
              <span style={{ fontSize: 9, color: T.t3 }}>CSV</span>
            </div>
          </div>
        </div>

        {/* Customer Cards */}
        {filtered.map((c, i) => (
          <MobileCard key={i} style={{ padding: "12px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{c.name}</div>
                <div style={{ fontSize: 10, color: T.accent, fontWeight: 600, marginTop: 1 }}>{c.id}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{c.total}</div>
                <StatusPill status={c.payment} />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <MIcon name="schedule" size={12} color={T.t3} />
              <span style={{ fontSize: 10, fontWeight: 600, color: T.t1 }}>Pickup: {c.slot}</span>
              <div style={{ marginLeft: "auto" }}><StatusPill status={c.fulfillment} /></div>
            </div>
            <div style={{ fontSize: 10, color: T.t2, marginBottom: 6 }}>{c.items}</div>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                <MIcon name="call" size={12} color={T.accent} fill={1} />
                <span style={{ fontSize: 9, color: T.accent, fontWeight: 600 }}>{c.phone}</span>
              </div>
            </div>
          </MobileCard>
        ))}

        <div style={{ fontSize: 10, color: T.t3, textAlign: "center", marginTop: 4 }}>
          Showing {filtered.length} of {MOCK.customerRunSheet.length}
        </div>
      </div>
    </>
  );
}

// ─── Main Mobile App Shell ───────────────────────────────────────────────────

export default function EventsPreOrderMobile() {
  const [screen, setScreen] = useState(DRILL_SCREENS.MAIN);

  const renderScreen = () => {
    switch (screen) {
      case DRILL_SCREENS.PREP_LIST:
        return <PrepListDrillIn onBack={() => setScreen(DRILL_SCREENS.MAIN)} />;
      case DRILL_SCREENS.PICKUP_SLOTS:
        return <PickupSlotsDrillIn onBack={() => setScreen(DRILL_SCREENS.MAIN)} />;
      case DRILL_SCREENS.BOOKING_CURVE:
        return <BookingCurveDrillIn onBack={() => setScreen(DRILL_SCREENS.MAIN)} />;
      case DRILL_SCREENS.CUSTOMER_SHEET:
        return <CustomerSheetDrillIn onBack={() => setScreen(DRILL_SCREENS.MAIN)} />;
      default:
        return <MainScreen onNavigate={setScreen} />;
    }
  };

  return (
    <div style={{ width: 375, margin: "0 auto", fontFamily: T.font, background: T.bg, minHeight: "100vh", display: "flex", flexDirection: "column", color: T.t1, position: "relative", overflow: "hidden", borderRadius: 20, boxShadow: "0 4px 32px rgba(0,0,0,0.12)" }}>
      {/* Google Fonts */}
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap" rel="stylesheet" />

      {/* Status Bar */}
      <div style={{ height: 44, background: T.surface, display: "flex", justifyContent: "center", alignItems: "flex-end", paddingBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>9:41</span>
      </div>

      {/* Screen Content */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {renderScreen()}
      </div>

      {/* Bottom Nav */}
      <BottomNav />
    </div>
  );
}
